// ST-32 -Claude- Brands list. Rows drill down to brand details.
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Card, EmptyState, LoadingSkeleton } from '@nearrme/ui-web';
import { listBrands, listCompanies } from '../api';
import { useAdminAuth } from '../useAdminAuth';
import { nextSort, SortHeader, sortRows, SortState } from '../tableSort';

export function AdminBrandsPage() {
  const { user } = useAdminAuth();
  const navigate = useNavigate();
  const [companyCode, setCompanyCode] = useState(user?.companyCode ?? '');
  const [sort, setSort] = useState<SortState<'brand' | 'category' | 'company' | 'status'>>({
    key: 'brand',
    direction: 'asc',
  });

  const companies = useQuery({ queryKey: ['admin', 'companies'], queryFn: listCompanies });
  const brands = useQuery({
    queryKey: ['admin', 'brands', companyCode],
    queryFn: () => listBrands(companyCode),
    enabled: !!companyCode,
  });

  const sortedBrands = sortRows(brands.data ?? [], sort, {
    brand: (brand) => brand.brandName,
    category: (brand) => brand.category,
    company: (brand) => brand.companyCode,
    status: (brand) => brand.isActive ? 1 : 0,
  });
  const sortHeader = (label: string, key: typeof sort.key) => (
    <SortHeader label={label} sortKey={key} sort={sort} onSort={(nextKey) => setSort((current) => nextSort(current, nextKey))} />
  );

  return (
    <div>
      <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Brands</h1>
      <p className="mt-1 text-slate-600 dark:text-slate-400">
        Brand records are clickable and open the brand detail page.
      </p>

      <div className="mt-4 flex items-center gap-3">
        <label className="text-sm font-medium text-slate-700 dark:text-slate-300">Company:</label>
        <select
          value={companyCode}
          onChange={(e) => setCompanyCode(e.target.value)}
          className="h-9 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        >
          <option value="">All companies</option>
          {(companies.data ?? []).map((c) => (
            <option key={c.companyCode ?? ''} value={c.companyCode ?? ''}>
              {c.companyCode} - {c.companyName}
            </option>
          ))}
        </select>
      </div>

      {brands.isLoading && companyCode && (
        <div className="mt-6 space-y-2">
          {[1, 2, 3, 4, 5].map((i) => <LoadingSkeleton key={i} className="h-12 w-full" />)}
        </div>
      )}

      {brands.error && (
        <Card padded className="mt-6">
          <div className="text-sm text-red-600 dark:text-red-400">{(brands.error as Error).message}</div>
        </Card>
      )}

      {brands.data && (
        <>
          <div className="mt-4 text-sm text-slate-500 dark:text-slate-400">
            {brands.data.length} {brands.data.length === 1 ? 'brand' : 'brands'}
          </div>
          {brands.data.length === 0 ? (
            <EmptyState title="No brands" description={`Company ${companyCode} has no active brands.`} />
          ) : (
            <Card padded={false} className="mt-2 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 bg-slate-50 dark:border-slate-800 dark:bg-slate-900">
                    <tr className="text-left">
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Brand', 'brand')}</th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Category', 'category')}</th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Company', 'company')}</th>
                      <th className="px-4 py-3 font-semibold text-slate-700 dark:text-slate-300">Description</th>
                      <th className="px-4 py-3 text-slate-700 dark:text-slate-300">{sortHeader('Status', 'status')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                    {sortedBrands.map((brand) => (
                      <tr
                        key={brand.brandId}
                        onClick={() => navigate(`/admin/brands/${brand.brandId}`)}
                        tabIndex={0}
                        role="link"
                        className="cursor-pointer hover:bg-slate-50 focus:bg-slate-50 focus:outline-none dark:hover:bg-slate-900 dark:focus:bg-slate-900"
                      >
                        <td className="px-4 py-3 font-semibold text-slate-900 dark:text-slate-100">{brand.brandName}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{brand.category ?? '-'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-slate-600 dark:text-slate-400">{brand.companyCode ?? '-'}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">{brand.description ?? '-'}</td>
                        <td className="px-4 py-3">
                          {brand.isActive ? (
                            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700 dark:bg-green-950 dark:text-green-400">Active</span>
                          ) : (
                            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600 dark:bg-slate-800 dark:text-slate-400">Inactive</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
